"use client";

import {
  apolloFormsSnippet,
  apolloSnippet,
  onlyOnTrackingHost,
  type TrackingSettings,
} from "@/lib/tracking";
import { useIsAdminRoute } from "./adminRoute";

// Site-wide tracking snippets, rendered from the root layout so they load on
// every page. Configured in /admin/settings (lib/trackingDb).
//
// Snippets are server-rendered into the initial HTML, so the browser executes
// them while parsing the document — the same as pasting them into the template.
// They do NOT re-run on client-side navigations, which is what trackers expect
// (Apollo's own loader watches route changes itself).
//
// Nothing here renders on /admin — see adminRoute. That covers the custom
// head/body boxes too, not just Apollo: those boxes are where the next pixel
// gets pasted, and a tag pasted into a box labelled "runs on every page" ought
// to mean every page of the WEBSITE. A verification meta tag loses nothing by
// being absent from a console that is noindex anyway.
export function TrackingScripts({
  tracking,
  slot,
}: {
  tracking: TrackingSettings;
  /** "head" renders at the top of <body>, "body" just before it closes. */
  slot: "head" | "body";
}) {
  const onAdmin = useIsAdminRoute();
  if (onAdmin) return null;

  if (slot === "head") {
    return (
      <>
        {tracking.apolloEnabled && tracking.apolloAppId && (
          <script
            id="apollo-tracker"
            dangerouslySetInnerHTML={{
              __html: onlyOnTrackingHost(apolloSnippet(tracking.apolloAppId)),
            }}
          />
        )}
        {/* After the tracker: the enrichment hides forms until it is ready,
            so the sooner it starts the shorter that is. */}
        {tracking.apolloFormsEnabled && tracking.apolloFormsAppId && (
          <script
            id="apollo-form-enrichment"
            dangerouslySetInnerHTML={{
              __html: onlyOnTrackingHost(apolloFormsSnippet(tracking.apolloFormsAppId)),
            }}
          />
        )}
        {tracking.headHtml.trim() && (
          <div
            hidden
            suppressHydrationWarning
            dangerouslySetInnerHTML={{ __html: tracking.headHtml }}
          />
        )}
      </>
    );
  }

  return tracking.bodyHtml.trim() ? (
    <div hidden suppressHydrationWarning dangerouslySetInnerHTML={{ __html: tracking.bodyHtml }} />
  ) : null;
}
